import React, {memo, useCallback, useEffect, useMemo} from "react";
import {NavigationProps} from "../../utils/navigationProps";
import {EditorActionBar} from "./actionBar/editorActionBar";
import {RootState} from "../../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import {cardEditorActions} from "./cardEditor.slice";
import {usePageTitle} from "../../hooks/usePageTitle";
import {cardQuestionText} from "../../utils/cardQuestionText";
import {FloatingButton} from "./floatingButton/floatingButton";
import {FloatingButtonIcon} from "./floatingButton/floatingButtons.interface";
import { styled } from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { useTheme } from "../../styles/ThemeContext";

type Props = NavigationProps & {
    cardId?: string;
};

const selector = (state: RootState) => ({
    markup: state.cardEditor.markup,
});

const Root = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
`;

const MainLayer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const TextArea = styled.textarea<ThemeProps>`
    width: calc(100% - 25px);
    height: calc(100% - 20px);
    margin: 10px;
    resize: none;
    border: ${props => props.theme.primaryBorder} 1px solid;
    outline: none;
    font-size: 1em;
    background-color: ${props => props.theme.primaryBackground};
    color: ${props => props.theme.inputText};

    &:focus {
        border: gray 1px solid;
    }
`;


const CardEditorComponent: React.FC<Props> = memo((props: Props) => {
    const dispatch = useDispatch();
    const { theme } = useTheme();

    useEffect(() => {
        dispatch(cardEditorActions.loadEditor(props.cardId!));
    }, [props.cardId, dispatch]);

    const onChangeHandler = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newText = event.target.value;
            dispatch(cardEditorActions.setMarkup(newText));
        },
        [dispatch]
    );

    const editHandler = useCallback(() => {
            dispatch(cardEditorActions.save());
        },
        [dispatch]);

    const stateProps = useSelector(selector);
    const savedMarkup = useSelector((state: RootState) =>
        props.cardId !== undefined ? state.cards[props.cardId]?.markup : undefined
    );
    const pageTitle = useMemo(() => {
        const q = savedMarkup !== undefined ? cardQuestionText(savedMarkup) : '';
        return `Edit: ${q.length > 0 ? q : 'Card'} · Memkeep`;
    }, [savedMarkup]);
    usePageTitle(pageTitle);

    return (
        <Root>
            <MainLayer>
                <EditorActionBar />
                <TextArea data-testid="card-markup-input" value={stateProps.markup} onChange={onChangeHandler} theme={theme} />
            </MainLayer>
            <FloatingButton icon={FloatingButtonIcon.Check} onClick={editHandler} testId="card-save" />
        </Root>
    );
});

CardEditorComponent.displayName = 'CardEditorComponent';

export { CardEditorComponent as CardEditor };
